<script setup lang="ts">
import type { Store } from '@/views/contests/Components/types/global'
import { filterStoresByScope } from '@/utils/audienceStoreIds'
import { cn } from '@/ui/lib/utils'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    class?: string
    maxShownItems?: number
    hasLabel: boolean
    disabled?: boolean
    storesWithPermission?: Store[]
    scopedStoreIds?: number[] | null
    showAllSelectedText?: boolean
    additionalStoreOptions?: {
      value: number
      label: string
    }[]
  }>(),
  {
    maxShownItems: 2,
    hasLabel: true,
    disabled: false,
    showAllSelectedText: true
  }
)

const { stores } = useLocalStorage()

const availableStores = computed(() => filterStoresByScope(stores.value, props.scopedStoreIds, (store) => store.id))

const storeOptions = computed(() => {
  const userStoreOptions = availableStores.value.map((store) => {
    const isDisabled =
      props.storesWithPermission &&
      !props.storesWithPermission?.some((storeWithPermission) => storeWithPermission.id === store.id)

    return {
      value: store.id,
      label: store.name,
      disabled: isDisabled,
      disabledReason: isDisabled ? t('global.multiselect.stores.storeNoPermission') : null
    }
  })

  const additionalOptions =
    props.additionalStoreOptions
      ?.filter((option) => !availableStores.value.some((store) => store.id === option.value))
      ?.map((option) => {
        return {
          value: option.value,
          label: option.label,
          disabled: true,
          disabledReason: t('global.multiselect.stores.userNoPermission')
        }
      }) ?? []

  return [...userStoreOptions, ...additionalOptions].sort((a, b) => (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0))
})

const modelValue = defineModel<number[]>('modelValue')

// Scoping can hide previously-selected stores from `availableStores` without deselecting them
// (e.g. CustomerListPage.vue deliberately never prunes `modelValue`), so the "N selected" summary
// must count only what's still listed, or it would overstate the selection.
const selectedCount = computed(
  () => modelValue.value.filter((id) => storeOptions.value.some((option) => option.value === id)).length
)

const allSelectedText = computed(() =>
  props.scopedStoreIds?.length || !props.showAllSelectedText ? null : t('global.multiselect.stores.allSelectedText')
)

const wrapperClass = computed(() => props.class)
</script>

<template>
  <div class="flex w-full flex-row items-center sm:w-auto">
    <template v-if="hasLabel">
      <BaseIcon name="IconoirFilterList" class="h-6 w-6 text-foreground" />
      <div class="item-center flex-row px-3 text-foreground">
        <span>{{ t('global.filters.filter') }}</span>
      </div>
    </template>
    <BaseMultiSelect
      v-model="modelValue"
      :disabled="disabled"
      :options="storeOptions"
      :max-selected-labels="maxShownItems"
      :class="cn('sm:w-72', wrapperClass)"
      :selected-items-label="
        t('global.multiselect.stores.storesSelected', {
          count: selectedCount
        })
      "
      :placeholder="t('global.multiselect.stores.placeholder')"
      :filter-placeholder="t('global.multiselect.stores.searchPlaceholder')"
      :empty-filter-message="t('global.multiselect.stores.noResultSearch')"
      :empty-message="t('global.multiselect.stores.noResult')"
      :all-selected-text="allSelectedText"
    >
      <template #option="{ option }">
        <div class="flex items-center gap-2">
          <div class="flex-1">{{ option.label }}</div>

          <div class="text-xs text-yellow-600">
            {{ option.disabledReason }}
          </div>
        </div>
      </template>
    </BaseMultiSelect>
  </div>
</template>
